import { AppBar, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, Divider, IconButton, Stack, Toolbar, Typography } from '@mui/material'
import { GridCloseIcon } from '@mui/x-data-grid'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useDispatch } from 'react-redux'
import { setEditTrainingsModal, setEditedExamQualifyData, setIsExamsEdited, setReloadAnalytics, setTrainingsToEdit } from '../../store/reducers/modal.reducer'
import { t } from 'i18next'
import { useExam } from '../../hooks/useExam'
import { capitalize, debounce, differenceBy, differenceWith, flatten, get, groupBy, isEmpty, isEqual, throttle, unionBy, uniqBy } from 'lodash'
import { TUser } from '../../shared/types/user.type'
import ListSkeleton from '../exams/loader/ListSkeleton'
import GlobalEditor from '../editTrainings/GlobalEditor'
import dayjs from 'dayjs'
import { useAuth } from '../../hooks/useAuth'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/it'
import 'dayjs/locale/tr'
import { Ttraining } from '../../shared/types/training.type'
import ExamRow from '../editTrainings/ExamRow'
import { TQualifyChoices } from '../../shared/types/qualifyChoices.type'
import { LoadingButton } from '@mui/lab'
import { toast } from 'react-toastify'
import toggleItemInArray, { getArrayDifference } from '../../shared/utils/arrays'
import { useQuery, useQueryClient } from 'react-query'
import { Exams as ApiExam } from '../../api/exams'
import EditTrainingsSkeleton from '../exams/loader/EditTrainingsSkeleton'
import { Icon } from '@iconify/react'
import { Add } from '@mui/icons-material'
import { hydraTransform } from '../../shared/utils/hydraTransformer'
import { useLocation } from 'react-router-dom'

export type TQualifyData = {
    "horse_shoeings"?: string,
    "riders"?: string,
    "track_conditions"?: string,
    "track_names"?: string,
    "track_surfaces"?: string,
    "training_types"?: string
}
function EditTrainingsModal() {
    //States
    const [selectedExams, setSelectedExams] = useState<Ttraining[]>([])
    const [Exams, setExams] = useState<Ttraining[]>([]) // the ones that we want to modify
    const [exams, _setExams] = useState<Ttraining[]>([]) // the original ones
    const [qualifyChoices, setQualifyChoices] = useState<TQualifyChoices | null>(null)
    const [page, setPage] = useState(1)
    const [maxPage, setMaxPage] = useState(0)
    const [examsLength, setExamsLength] = useState(0)
    const itemsPerPage = 30
    const queryClient = useQueryClient()
    const [qualifyData, setQualifyData] = useState<TQualifyData>({
        "horse_shoeings": "",
        "riders": "",
        "track_conditions": "",
        "track_names": "",
        "track_surfaces": "",
        "training_types": ""
    })
    //Hooks
    const open = useSelector<RootState>(state => state.modals.editTrainingsModal) as boolean
    const lastEditedExam = useSelector<RootState>(state => state.modals.editedExamQualifyData) as TQualifyData | null
    const dispatch = useDispatch()
    const auth = useSelector<RootState>(state => state.user.user) as TUser | null
    const { getQualifyChoices, editTrainings } = useExam(false, get(auth, '@id'), null, undefined, ['additional-groups[]=qualificationDetails'])
    const trainingsToEdit = useSelector<RootState>(state => state.modals.trainingsToEdit) as number[]



    const mainContainer = useRef<HTMLDivElement | null>(null)
    const boxRef = useRef<HTMLDivElement | null>(null)
    const lastY = useRef(0)

    const trainingsToEditParam = useMemo(() => {

        if (isEmpty(trainingsToEdit)) return []
        let arr: string[] = []
        trainingsToEdit.forEach(training => {
            arr.push(`id[]=${training}`)
        })
        return arr

    }, [trainingsToEdit])




    //Queries
    const query = useQuery(['trainingExams'], () => ApiExam.getExams(get(auth, '@id'), undefined, ['additional-groups[]=qualificationDetails', 'include-total-count=true', 'itemsPerPage=15', "exclude_demo=true", `exists[horseTrainer.horse]=1`, ...trainingsToEditParam], page), {
        enabled: false,
        refetchOnWindowFocus: false,
        cacheTime: 900000,
        onSuccess: ({ data }) => {
            let dataExams = hydraTransform(data)
            // _setExams(prev => uniqBy([...unionBy(Exams, prev, 'id'), ...dataExams], e => e.id))
            _setExams(prev => [...prev, ...dataExams])
            let total_items = get(data, 'hydra:totalItems', 0)
            setExamsLength(total_items)
            setMaxPage(Math.round(total_items / itemsPerPage))

        }
    })
    //Effects


    const scrollHandler = useCallback((event: Event) => {
        const scrollTop = mainContainer.current?.scrollTop || 0;


        if (scrollTop > 20) {
            boxRef.current?.classList.add('editorStick');
            lastY.current = scrollTop;
        } else {
            lastY.current = scrollTop;
            boxRef.current?.classList.remove('editorStick');

        }


    }, [query.isLoading, query.isFetching])

    useEffect(() => {
        if (!mainContainer.current || !boxRef.current) return;

        const throttledScroll = throttle(scrollHandler, 100);

        mainContainer.current.addEventListener('scroll', throttledScroll);


        return () => {
            mainContainer.current?.removeEventListener('scroll', throttledScroll);
            throttledScroll.cancel?.(); // cancel any pending throttled calls
        }
    }, [scrollHandler]);

    useEffect(() => {
        if (!open) return
        getExams()
    }, [page, open])

    useEffect(() => {
        if (lastEditedExam) {
            setQualifyData(lastEditedExam)
        }
    }, [lastEditedExam])

    const groupedExams = useMemo(() => {
        if (isEmpty(Exams)) return []
        return groupBy(Exams, (item: Ttraining) => dayjs(item.creation_date).locale(auth?.lang ?? 'en').format('dddd D MMMM'))

    }, [Exams])

    useEffect(() => {
        if (!isEmpty(exams) && !isEmpty(qualifyChoices)) {
            let trackConditions = exams.map(e => e?.track_condition).filter(e => e)
            let trackSurface = exams.map(e => e?.track_surface).filter(e => e)
            let trackName = exams.map(e => e?.track_name).filter(e => e)
            let rider = exams.map(e => e?.rider).filter(e => e)
            let trainingType = exams.map(e => e?.training_type).filter(e => e)

            let tempQualify: any = qualifyChoices


            tempQualify.track_conditions = uniqBy([...tempQualify.track_conditions, ...trackConditions], '@id')
            tempQualify.track_surfaces = uniqBy([...tempQualify.track_surfaces, ...trackSurface], '@id')
            tempQualify.track_names = uniqBy([...tempQualify.track_names, ...trackName], '@id')
            tempQualify.riders = uniqBy([...tempQualify.riders, ...rider], '@id')
            tempQualify.training_types = uniqBy([...tempQualify.training_types, ...trainingType], '@id')



            setQualifyChoices(tempQualify)

        }
    }, [exams, qualifyChoices])


    useEffect(() => {
        setExams((prev) => {
            // Find exams that are in `exams` but not in `prev`
            const newExams = differenceBy(exams, prev, 'id');
            return [...prev, ...newExams];  // Add the new exams to `prev`
        });
    }, [exams])

    useEffect(() => {

        if (!isEmpty(selectedExams)) updateExams(selectedExams)
    }, [Exams.length, selectedExams])


    useEffect(() => {
        if (!open) {
            dispatch(setEditedExamQualifyData(null))
            setSelectedExams([])
            clearQualifyData()
            setPage(1)
            _setExams([])
            return
        }
        getQualifyChoices.mutateAsync(auth?.id)
            .then(({ data }) => {

                setQualifyChoices(data)
            }).catch(err => {
                console.log({ err });
            })
    }, [open])

    useEffect(() => {

        if (!isEmpty(selectedExams)) updateExams(selectedExams)

    }, [selectedExams, qualifyData])


    const getExams = debounce(() => {
        query.refetch()
    }, 800)

    const updateExams = (selectedExams: Ttraining[]) => {
        if (isEmpty(selectedExams)) return
        let selectedIds = selectedExams.map(e => e.id)

        let tempExams: any = Exams

        // Track Conditions 
        let trackCondition = qualifyData.track_conditions
        if (trackCondition) {
            let findCondition = qualifyChoices?.track_conditions.find(e => e['@id'] === trackCondition)

            if (findCondition) {
                tempExams = tempExams.map((e: Ttraining) => selectedIds.includes(e.id) ? { ...e, track_condition: findCondition } : e)
            }
        }

        // Track Name 
        let trackName = qualifyData.track_names
        if (trackName) {
            let findCondition = qualifyChoices?.track_names.find(e => e['@id'] === trackName)

            if (findCondition) {
                tempExams = tempExams.map((e: Ttraining) => selectedIds.includes(e.id) ? { ...e, track_name: findCondition } : e)
            }
        }

        // Track Name 
        let trainingType = qualifyData.training_types
        if (trainingType) {
            let findCondition = qualifyChoices?.training_types.find(e => e['@id'] === trainingType)

            if (findCondition) {
                tempExams = tempExams.map((e: Ttraining) => selectedIds.includes(e.id) ? { ...e, training_type: findCondition } : e)
            }
        }

        // Rider
        let rider = qualifyData.riders
        if (rider) {
            let findCondition = qualifyChoices?.riders.find(e => e['@id'] === rider)

            if (findCondition) {
                tempExams = tempExams.map((e: Ttraining) => selectedIds.includes(e.id) ? { ...e, rider: findCondition } : e)
            }
        }

        // Surface
        let surface = qualifyData.track_surfaces
        if (surface) {
            let findCondition = qualifyChoices?.track_surfaces.find(e => e['@id'] === surface)

            if (findCondition) {
                tempExams = tempExams.map((e: Ttraining) => selectedIds.includes(e.id) ? { ...e, track_surface: findCondition } : e)
            }
        }

        setExams(tempExams)
    }

    const handleClose = () => {
        dispatch(setEditTrainingsModal(false))
        dispatch(setTrainingsToEdit([]))
        setSelectedExams([])
        setExams([])
        _setExams([])
    }

    const handleDateCheck = useCallback((checked: boolean, data: [string, Ttraining[]]) => {

        if (checked) {

            let tempSelectedExams: Ttraining[] = uniqBy([...selectedExams, ...data[1]], e => e.id)
            setSelectedExams(tempSelectedExams)

        } else {
            let ids = data[1].map(e => e?.id)
            setSelectedExams(prev => uniqBy(prev.filter(e => !ids.includes(e.id)), e => e.id))
        }
    }, [selectedExams])

    const isDateChecked = (exams: Ttraining[]) => {
        let ids = exams.map(e => e?.id)

        return selectedExams.filter(e => ids.includes(e.id)).length === ids.length
    }

    const isExamChecked = useCallback((exam: Ttraining) => {

        return selectedExams.find(e => e?.id === exam?.id) ? true : false
    }, [selectedExams])


    const HandleExamClick = useCallback((checked: boolean, exam: Ttraining) => {
        let _selectedExams = [...selectedExams]
        if (checked) {
            _selectedExams.push(exam)
        } else {
            _selectedExams = _selectedExams.filter(e => e.id !== exam.id)
        }
        setSelectedExams(_selectedExams)
    }, [selectedExams])


    const updateExam = useCallback((key: string, value: any, id: number) => {
        setExams(prev => prev.map(e => e.id === id ? { ...e, [key]: value } : e))
    }, [])


    const onUpdateQualifyChoices = useCallback((key: keyof TQualifyChoices, value: any) => {
        // key is something like "track_names", value is the new object to add
        setQualifyChoices(prev => {
            if (!prev) return null

            const updatedArray = [...prev[key], value] // append new value
            const uniqueArray = uniqBy(updatedArray, '@id') // deduplicate by @id

            return { ...prev, [key]: uniqueArray }
        })
    }, [])

    const updateQualifyData = useCallback((key: keyof TQualifyData, value?: string) => {

        if (key === 'track_names') {
            let find = qualifyChoices?.track_names.find(i => i['@id'] === value)

            if (find?.track_surface) {
                setQualifyData(prev => ({ ...prev, [key]: value, 'track_surfaces': typeof find?.track_surface === 'string' ? find?.track_surface : find?.track_surface['@id'] }))
                return
            }
        }

        setQualifyData(prev => ({ ...prev, [key]: value }))
    }, [qualifyChoices])

    const clearQualifyData = () => {
        setQualifyData({
            "horse_shoeings": "",
            "riders": "",
            "track_conditions": "",
            "track_names": "",
            "track_surfaces": "",
            "training_types": ""
        })
    }



    const prepareData = () => {
        if (isEmpty(Exams)) return []

        let modifiedExams = getArrayDifference(Exams, exams)

        //If there is no modified exams we close the modal
        if (isEmpty(modifiedExams)) {
            handleClose()
            return
        }

        let data = modifiedExams.map((e: Ttraining & any) => {
            return {
                training_id: e.id,
                input: {
                    ...!isEmpty(e?.new_training_type) ? { new_training_type: e?.new_training_type } : { training_type: e.training_type?.['@id'] },
                    ...!isEmpty(e?.new_track_condition) ? { new_track_condition: e?.new_track_condition } : { track_condition: e.track_condition?.['@id'] },
                    ...!isEmpty(e?.new_track_name) ? { new_track_name: e?.new_track_name } : { track_name: e.track_name?.['@id'] },
                    ...!isEmpty(e?.new_rider) ? { new_rider: e?.new_rider } : { rider: e.rider?.['@id'] },
                    "track_surface": e.track_surface?.['@id'],
                    "comment": e.comment,
                    "is_reference": e?.is_reference,
                }
            }
        })




        return { items: data }

    }

    const saveData = () => {
        let data = prepareData()

        //If there is no exam modified we close directly
        if (!data) {
            handleClose()
            return

        }

        //If this modal is open from analytics
        //we reload fetchAnalytics inside analytics
        const isAnalyticsRoute = window.location.pathname.includes("/analytics");


        editTrainings.mutateAsync(data)
            .then(result => {



                dispatch(setReloadAnalytics(isAnalyticsRoute))


                toast(t('rights:success'), { type: 'success' })
                setSelectedExams([])
                clearQualifyData()
                handleClose()

                dispatch(setIsExamsEdited(true))


            }).catch(err => {
                toast(t('error:generic'), { type: 'error' })
            })
    }

    const loadMore = () => {
        if (maxPage === page) return
        setPage(prev => prev + 1)
    }

    const onToggleExams = useCallback((checked: boolean) => {
        if (!checked) {
            setSelectedExams([])
        } else {
            let exams = Object.values(groupedExams)
            if (!isEmpty(exams)) {
                setSelectedExams(flatten(exams))
            }

        }
    }, [groupedExams])

    if (!open) return null



    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display={'flex'} alignItems={'center'}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <GridCloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" fontWeight={400} component="div">
                            {t('editTrainingForm:editMultipleTrainings')}
                        </Typography>
                    </Box>

                    <LoadingButton onClick={saveData} loading={editTrainings.isLoading} variant='contained'>{t('button:save')}</LoadingButton>

                </Toolbar>

            </AppBar>

            <DialogContent
                ref={mainContainer}
                sx={{ position: 'relative', p: 0, minHeight: '100%', pb: 10 }}
            >
                <Box ref={boxRef} px={4} pt={2}>
                    <GlobalEditor isVisible={!isEmpty(selectedExams)} onToggleExams={onToggleExams} qualifyData={qualifyData} onChangeSelect={updateQualifyData} loading={getQualifyChoices.isLoading} qualifyChoices={qualifyChoices}></GlobalEditor>
                </Box>
                <Stack direction={'column'} spacing={2} p={4}>

                    {
                        Object.entries(groupedExams).map((date, index) => (
                            <React.Fragment key={index}>
                                <Divider></Divider>
                                <Typography variant='subtitle2' fontSize={'15px'}>
                                    <Checkbox checked={isDateChecked(date[1])} onChange={(_, checked) => handleDateCheck(checked, date)}></Checkbox>
                                    {capitalize(get(date, '[0]', ''))}
                                </Typography>


                                <Stack spacing={2}>
                                    {date[1].map(exam => (
                                        <ExamRow
                                            key={exam.id}
                                            updateExam={updateExam}
                                            handleInputChange={HandleExamClick}
                                            qualifyChoices={qualifyChoices}
                                            isExamChecked={isExamChecked(exam)}
                                            exam={exam}
                                            onUpdateQualifyChoices={onUpdateQualifyChoices}
                                        />
                                    ))}
                                </Stack>
                            </React.Fragment>
                        ))
                    }
                </Stack>
                {
                    !isEmpty(trainingsToEdit) ?
                        <>
                            {
                                (query.isLoading || query.isFetching) &&
                                <Box display={'flex'} my={2} justifyContent={'center'} alignItems={'center'}>
                                    <CircularProgress size={13} color='inherit'></CircularProgress>
                                </Box>
                            }
                        </>

                        : <Box display={'flex'} justifyContent={'center'} mt={5}>
                            <LoadingButton disabled={maxPage === page} onClick={loadMore} startIcon={<Add></Add>} loading={query.isLoading || query.isFetching}>{t('loadMore')}</LoadingButton>

                        </Box>
                }

            </DialogContent>
        </Dialog>
    )
}

export default EditTrainingsModal